/**
 * Bulk charge fundraising guarantee
 *
 * System settings screen (system context, write access) for charging the
 * fundraising guarantee shortfall to a batch of PlaceHub ambassadors. Replaces
 * issuing these by hand, one curl call at a time.
 *
 * The design bias is refuse rather than guess. Rows the client cannot verify
 * are blocked rather than charged, and responses the API cannot make definite
 * are reported as unknown rather than as success or failure.
 */

import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Button, Loader, Message, Progress, Segment } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import fileDownload from 'js-file-download';
import { v4 as uuidv4 } from 'uuid';
import { chargeEventAmbassador, fetchEventAmbassador } from './api';
import {
  CONSECUTIVE_FAILURE_ABORT_THRESHOLD,
  MAX_IDS,
  SUMMARY_FETCH_CONCURRENCY,
  chargePhase,
  eligibilityReason,
  eligibilityVerdict,
  i18nKeys,
  outcomeClass,
  rowOutcome
} from './constants';
import {
  buildCsvFilename,
  buildIdempotencyKey,
  buildResultsCsv,
  chunk,
  classifyChargeOutcome,
  computeAmountToCharge,
  countByOutcome,
  deriveRowEligibility,
  formatAmountWithCurrency,
  groupSummaryRows,
  isChargeable,
  parseAmbassadorIds,
  sumChargeableByCurrency
} from './helpers';
import { patchOutcome, readLedger, recordAttempt } from './recentCharges';
import { bulkChargeReducer, initialState } from './reducer';
import { CharityGroup, EventAmbassador, RecentChargeEntry, SummaryRow } from './types';
import IdInputPanel from './components/IdInputPanel';
import SummaryTable from './components/SummaryTable';
import ChargeConfirmPanel from './components/ChargeConfirmPanel';
import ResultsSummary from './components/ResultsSummary';
import StartNewBatchModal from './components/StartNewBatchModal';
import styles from './BulkChargeGuarantee.module.css';

const BulkChargeGuarantee: React.FC = () => {
  const [state, dispatch] = useReducer(bulkChargeReducer, initialState);

  /*
   * The ledger snapshot is held in state rather than read during render, so
   * eligibility and the countdown copy stay stable while the operator reads the
   * table instead of shifting under them.
   */
  const [recentEntries, setRecentEntries] = useState<RecentChargeEntry[]>([]);
  const [ledgerReadAt, setLedgerReadAt] = useState(() => Date.now());
  const [newBatchModalOpen, setNewBatchModalOpen] = useState(false);

  /*
   * Synchronous guard against a double fired charge. A `state.phase` read is
   * not reliable inside a single tick under React 18 batching, so the ref is
   * what actually prevents a second run, and the disabled button is the
   * cosmetic half of it.
   */
  const chargeInFlight = useRef(false);
  const stopRequestedRef = useRef(false);

  const rowList = useMemo(
    () => state.ids.map((id) => state.rows[id]).filter(Boolean) as SummaryRow[],
    [state.ids, state.rows]
  );

  /* Charity and event live on group headers rather than repeating per row. */
  const groups = useMemo(() => groupSummaryRows(rowList), [rowList]);

  const chargeableRows = useMemo(() => rowList.filter(isChargeable), [rowList]);
  const blockedCount = rowList.length - chargeableRows.length;
  const counts = useMemo(() => countByOutcome(rowList), [rowList]);

  /*
   * Never one summed figure. A batch can span charities, each charity has its
   * own currency, and adding across them would put a wrong number on the panel
   * where the operator confirms the charges.
   */
  const totalsByCurrency = useMemo(() => sumChargeableByCurrency(rowList), [rowList]);

  const recentlyBlockedCount = useMemo(
    () =>
      rowList.filter((row) =>
        row.eligibility.reasons.includes(eligibilityReason.chargedRecently)
      ).length,
    [rowList]
  );

  // Warn on navigation while charges are in flight. It cannot stop the server,
  // but losing the tab means losing the record of what was attempted.
  useEffect(() => {
    if (state.phase !== chargePhase.charging) {
      return undefined;
    }
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = I18n.t(i18nKeys.PROGRESS.LEAVE_WARNING);
      return event.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state.phase]);

  const buildRow = useCallback(
    (
      id: number,
      ambassador: EventAmbassador | null,
      entries: RecentChargeEntry[],
      now: number
    ): SummaryRow => {
      const eligibility = deriveRowEligibility(ambassador, entries, now);
      return {
        id,
        ambassador,
        fetchError: ambassador ? null : I18n.t(i18nKeys.ERRORS.ROW_LOAD_FAILED),
        eligibility,
        amount: computeAmountToCharge(ambassador),
        outcome:
          eligibility.verdict === eligibilityVerdict.blocked
            ? rowOutcome.blocked
            : rowOutcome.pending,
        errorCode: null,
        httpStatus: null,
        attemptedAtUtc: null,
        verification: null,
        isRefreshing: false
      };
    },
    []
  );

  const handleGenerate = useCallback(async () => {
    dispatch({ type: 'GENERATE_START' });

    const now = Date.now();
    const ledger = readLedger(now);
    setRecentEntries(ledger.entries);
    setLedgerReadAt(now);

    // GENERATE_START is queued, so derive the id list here exactly the way the
    // reducer does rather than reading state back mid-tick.
    const ids = parseAmbassadorIds(state.rawInput).ids.slice(0, MAX_IDS);

    if (!ids.length) {
      dispatch({ type: 'GENERATE_FAILURE', error: I18n.t(i18nKeys.FORM.VALIDATION_NO_IDS) });
      return;
    }

    try {
      const rows: SummaryRow[] = [];
      // Reads are safe to parallelise; the charges deliberately are not.
      for (const batch of chunk(ids, SUMMARY_FETCH_CONCURRENCY)) {
        const settled = await Promise.allSettled(batch.map((id) => fetchEventAmbassador(id)));
        settled.forEach((result, index) => {
          const id = batch[index];
          rows.push(
            buildRow(
              id,
              result.status === 'fulfilled' ? result.value : null,
              ledger.entries,
              now
            )
          );
        });
      }
      dispatch({ type: 'GENERATE_SUCCESS', rows });
    } catch (error: any) {
      dispatch({
        type: 'GENERATE_FAILURE',
        error: error?.message || I18n.t(i18nKeys.ERRORS.LOAD_FAILED)
      });
    }
  }, [state.rawInput, buildRow]);

  /**
   * The charge loop.
   *
   * Strictly sequential. A systemic fault fails the first row and the operator
   * can stop before the rest; at any concurrency several charges are already in
   * flight before anyone can react. The stop flag is checked between rows only,
   * because aborting an in flight request does not stop the server charging, it
   * only destroys our knowledge of the outcome.
   */
  const runChargeLoop = useCallback(
    async (rows: SummaryRow[], batchId: string) => {
      let consecutiveIndeterminate = 0;

      for (const row of rows) {
        if (stopRequestedRef.current) {
          break;
        }

        dispatch({ type: 'ROW_CHARGING', id: row.id });

        // Recorded BEFORE the request, so a crashed tab still leaves a trace.
        const attemptedAt = Date.now();
        recordAttempt(row.id, attemptedAt);

        const result = await chargeEventAmbassador(
          row.id,
          buildIdempotencyKey(batchId, row.id)
        );
        const outcome = classifyChargeOutcome(result.httpStatus, result.errorCode);

        patchOutcome(row.id, outcome, Date.now());

        dispatch({
          type: 'ROW_RESULT',
          id: row.id,
          outcome,
          errorCode: result.errorCode,
          httpStatus: result.httpStatus,
          attemptedAtUtc: new Date(attemptedAt).toISOString()
        });

        // Verify against the record rather than trusting the status code: the
        // API reports success even when the Stripe charge was not paid, and
        // the charge fields are only written when it was.
        try {
          const refreshed = await fetchEventAmbassador(row.id);
          dispatch({
            type: 'ROW_VERIFIED',
            id: row.id,
            verification: {
              chargedViaDonationPlan: refreshed.chargedViaDonationPlan ?? null,
              donationPlanChargedAmount: refreshed.donationPlanChargedAmount ?? null
            }
          });
        } catch {
          // Verification is best effort. Failing to read it back does not
          // change what the charge call reported.
        }

        // Circuit breaker: a systemic fault (Stripe down, credentials rejected)
        // should not chew through the rest of the batch.
        consecutiveIndeterminate =
          outcome === outcomeClass.indeterminate ? consecutiveIndeterminate + 1 : 0;
        if (consecutiveIndeterminate >= CONSECUTIVE_FAILURE_ABORT_THRESHOLD) {
          break;
        }
      }

      dispatch({ type: 'CHARGE_FINISHED' });
      setRecentEntries(readLedger(Date.now()).entries);
    },
    []
  );

  const handleCharge = useCallback(() => {
    if (chargeInFlight.current) {
      return;
    }
    chargeInFlight.current = true;
    stopRequestedRef.current = false;

    const batchId = uuidv4();
    dispatch({ type: 'CONFIRM_CHARGE', batchId });

    const rows = rowList.filter(isChargeable);
    runChargeLoop(rows, batchId).finally(() => {
      chargeInFlight.current = false;
    });
  }, [rowList, runChargeLoop]);

  const handleStop = useCallback(() => {
    stopRequestedRef.current = true;
    dispatch({ type: 'REQUEST_STOP' });
  }, []);

  const handleRefreshRow = useCallback(
    async (id: number) => {
      dispatch({ type: 'ROW_REFRESH_START', id });
      const now = Date.now();
      const ledger = readLedger(now);
      setRecentEntries(ledger.entries);
      setLedgerReadAt(now);

      let ambassador: EventAmbassador | null = null;
      try {
        ambassador = await fetchEventAmbassador(id);
      } catch {
        ambassador = null;
      }

      dispatch({
        type: 'ROW_REFRESHED',
        id,
        ambassador,
        eligibility: deriveRowEligibility(ambassador, ledger.entries, now),
        amount: computeAmountToCharge(ambassador),
        verification: ambassador
          ? {
              chargedViaDonationPlan: ambassador.chargedViaDonationPlan ?? null,
              donationPlanChargedAmount: ambassador.donationPlanChargedAmount ?? null
            }
          : null
      });
    },
    []
  );

  /*
   * Stable identities so the memoised rows actually hold. Without these, every
   * per row charge result would re-render all 100 rows.
   */
  const handleRemoveId = useCallback((id: number) => {
    dispatch({ type: 'REMOVE_ID', id });
  }, []);

  /**
   * One charity's rows only.
   *
   * This is the file that gets sent on, so it must never contain another
   * charity's ambassadors. The charity name goes in the filename because
   * several of these downloaded minutes apart would otherwise differ only by
   * their timestamp, sitting in a folder about to be attached to several
   * different emails.
   */
  const handleDownloadCharity = useCallback((group: CharityGroup) => {
    fileDownload(
      buildResultsCsv(group.rows),
      buildCsvFilename(new Date(), group.charityName),
      'text/csv;charset=utf-8'
    );
  }, []);

  const isLoading = state.phase === chargePhase.loadingSummary;
  const isLocked = state.phase === chargePhase.charging || state.phase === chargePhase.done;
  const showSummary = rowList.length > 0 && !isLoading;

  /*
   * Progress counts the rows this run set out to charge. Blocked rows were
   * never in the run, and a row mid-flight is in neither the done nor the
   * pending bucket, so count the attempt set rather than adding the two up.
   */
  const attemptCount = rowList.filter((row) => row.outcome !== rowOutcome.blocked).length;
  const chargedCount = counts.submitted + counts.rejected + counts.indeterminate;

  return (
    <div className={styles.container}>
      <h2>
        <Translate value={i18nKeys.TITLE} />
      </h2>

      <Segment className={styles.explanationSegment}>
        <h4>
          <Translate value={i18nKeys.EXPLANATION.TITLE} />
        </h4>
        <div className={styles.explanationContent}>
          <p>
            <Translate value={i18nKeys.EXPLANATION.BODY} />
          </p>
          <p>
            <Translate value={i18nKeys.EXPLANATION.ONE_AT_A_TIME} />
          </p>
          <p>
            <Translate value={i18nKeys.EXPLANATION.BLOCKED_ROWS} />
          </p>
          <p>
            <strong>
              <Translate value={i18nKeys.EXPLANATION.IRREVERSIBLE} />
            </strong>
          </p>
        </div>
      </Segment>

      <IdInputPanel
        rawInput={state.rawInput}
        ids={state.ids}
        parseFeedback={state.parseFeedback}
        isLoading={isLoading}
        isLocked={isLocked}
        showStaleWarning={state.summaryInvalidated}
        showChips={!showSummary}
        onChange={(value) => dispatch({ type: 'SET_RAW_INPUT', rawInput: value })}
        onGenerate={handleGenerate}
        onClear={() => dispatch({ type: 'CLEAR' })}
        onRemoveId={(id) => dispatch({ type: 'REMOVE_ID', id })}
      />

      {state.loadError && (
        <Message negative>
          <Message.Header>
            <Translate value={i18nKeys.ERRORS.LOAD_FAILED} />
          </Message.Header>
          <p>{state.loadError}</p>
        </Message>
      )}

      {isLoading && (
        <div className={styles.loadingContainer}>
          <Loader active inline="centered" />
        </div>
      )}

      {recentlyBlockedCount > 0 && (
        <Message warning>
          <p>{I18n.t(i18nKeys.RECENT.BANNER, { count: recentlyBlockedCount })}</p>
          <p>
            <Translate value={i18nKeys.RECENT.PER_BROWSER_NOTE} />
          </p>
        </Message>
      )}

      {showSummary && (
        <>
          {/* A 100 row table is hard to read at a glance, so lead with the counts. */}
          <div className={styles.countsBar}>
            <span className={styles.countsPrimary}>
              {I18n.t(i18nKeys.TABLE.READY_COUNT, { count: chargeableRows.length })}
            </span>
            {blockedCount > 0 && (
              <span>{I18n.t(i18nKeys.TABLE.BLOCKED_COUNT, { count: blockedCount })}</span>
            )}
            {totalsByCurrency.map((total) => (
              <span key={total.currency ?? 'default'}>
                {I18n.t(i18nKeys.TABLE.TOTAL, {
                  total: formatAmountWithCurrency(total.amount, total.currency),
                  count: total.count
                })}
              </span>
            ))}
          </div>

          <SummaryTable
            groups={groups}
            recentEntries={recentEntries}
            now={ledgerReadAt}
            canRemove={!isLocked}
            showOutcome={state.phase === chargePhase.charging || state.phase === chargePhase.done}
            onRemoveId={handleRemoveId}
            onRefreshId={handleRefreshRow}
            onDownloadCharity={handleDownloadCharity}
          />

          {state.phase === chargePhase.summary && (
            <ChargeConfirmPanel
              chargeableCount={chargeableRows.length}
              blockedCount={blockedCount}
              totalsByCurrency={totalsByCurrency}
              confirmListCorrect={state.confirmListCorrect}
              confirmIrreversible={state.confirmIrreversible}
              onToggleListCorrect={(value) =>
                dispatch({ type: 'SET_CONFIRM', field: 'confirmListCorrect', value })
              }
              onToggleIrreversible={(value) =>
                dispatch({ type: 'SET_CONFIRM', field: 'confirmIrreversible', value })
              }
              onCharge={handleCharge}
            />
          )}

          {state.phase === chargePhase.charging && (
            <Segment>
              {/* A 100 row run takes several minutes, so show real progress. */}
              <Progress
                value={chargedCount}
                total={attemptCount || 1}
                progress="ratio"
                indicating
                size="small"
              />
              <div className={styles.progressRow}>
                <Loader active inline size="small" />
                <span>
                  {I18n.t(i18nKeys.PROGRESS.RUNNING, {
                    done: chargedCount,
                    total: attemptCount
                  })}
                </span>
                <Button
                  size="small"
                  onClick={handleStop}
                  disabled={state.stopRequested}
                >
                  <Translate
                    value={
                      state.stopRequested
                        ? i18nKeys.PROGRESS.STOPPING
                        : i18nKeys.PROGRESS.STOP_BUTTON
                    }
                  />
                </Button>
              </div>
            </Segment>
          )}

          {state.phase === chargePhase.done && (
            <ResultsSummary
              counts={counts}
              wasStopped={state.stopRequested}
              abortedByBreaker={state.abortedByBreaker}
              onStartNewBatch={() => setNewBatchModalOpen(true)}
            />
          )}

        </>
      )}

      <StartNewBatchModal
        isOpen={newBatchModalOpen}
        charityCount={groups.length}
        onConfirm={() => {
          setNewBatchModalOpen(false);
          dispatch({ type: 'START_NEW_BATCH' });
        }}
        onCancel={() => setNewBatchModalOpen(false)}
      />
    </div>
  );
};

export default BulkChargeGuarantee;
